import { PageFilterSource } from '../types/page-filter-source';
import { PageFilterGroup } from '../types/page-filter-group';
import { PageFilterLink } from '../types/page-filter-link';
import { PageFilterItem } from '../types/page-filter-item';
import { effect, linkedSignal, signal } from '@angular/core';
import { FilterState } from '../../filter.state';
import { Filter } from '../../types/filter';
import { normalizeFilter } from '../../helper/normalize-filter.function';
import { createToggleFilter } from '../functions/page-filter-toggle.function';

export class PageFilterService {

  opened = signal(false);
  view = signal<'filter' | 'templates'>('filter');
  tab = signal<'list' | 'custom'>('list');

  states = linkedSignal(() => {
    return this.sources.map(source => {
      const state = new FilterState(source.state.config);
      return state;
    });
  })

  constructor(public sources: PageFilterSource<any>[],
              public groups: PageFilterGroup[],
              public links: PageFilterLink<any, any>[],
              public items: PageFilterItem<any, any>[],
              public key: string | null = null) {
    for (const source of this.sources) {
      effect(() => {
        const sourceState = source.state.state();
        const state = this.states().find(o => o.config === source.state.config);
        if(!state)
          return;
        state.set(sourceState as Filter<any>);
      });
    }
    if(this.key) {
      for(const source of this.sources) {
        if(!source.state.config.key)
          console.warn(`[PageFilterService] Side filter '${this.key}' has a source without a filter key - it will be skipped in filter templates`);
      }
    }
  }

  applyFilter(): void {
    for(const filter of this.states()) {
      const source = this.sources.find(o => o.state.config === filter.config);
      if(source)
        source.state.set(filter.state() as Filter<any>);
      else
        console.warn(`[PageFilterService:applyFilter] Cannot find source for filter with config`);
    }
  }

  resetFilter(): void {
    for(const source of this.sources) {
      source.state.reset();
    }
  }

  /** Top-level value of an item in its committed source state (list-tab semantics). */
  getSourceItemValue(item: PageFilterItem<any, any>): Filter<any> | null {
    return this.getItemValue(item, item.source.state);
  }

  /** Top-level value of an item in the sidebar's draft state (reads the state signal). */
  getDraftItemValue(item: PageFilterItem<any, any>): Filter<any> | null {
    const state = this.getItemState(item, 'draft');
    return state ? this.getItemValue(item, state) : null;
  }

  /** true when the state holds EXACTLY the toggle item's configured filter (uuid ignored). */
  isToggleItemActive(item: PageFilterItem<any, any>, target: 'draft' | 'source'): boolean {
    const state = this.getItemState(item, target);
    const current = state ? this.getItemValue(item, state) : null;
    if(!current)
      return false;
    return (
      JSON.stringify(normalizeFilter(current)) ===
      JSON.stringify(normalizeFilter(createToggleFilter(item)))
    );
  }

  /** Flips a toggle item: active -> remove; inactive/deviating -> write the configured filter. */
  toggleItemFilter(item: PageFilterItem<any, any>, target: 'draft' | 'source'): void {
    const filter = this.isToggleItemActive(item, target) ? null : createToggleFilter(item);
    this.applyItemFilter(item, filter, target);
  }

  /**
   * Writes an item's filter value (replace/remove/add + link propagation).
   * target 'draft' edits the sidebar's working copies, 'source' commits
   * directly (menu-bar edits) - the constructor effects re-sync the drafts.
   */
  applyItemFilter(
    item: PageFilterItem<any, any>,
    filter: Filter<any> | null,
    target: 'draft' | 'source'
  ): void {
    const state = this.getItemState(item, target);
    if(!state)
      throw new Error('[PageFilterService:applyItemFilter] State not found');
    this.writeItemFilter(state, item, filter);

    const links = this.links.filter(o => o.origin.field === item.field && o.origin.config === item.config);
    for (const link of links) {
      for(const linkTarget of link.targets) {
        const targetState = this.getItemState(linkTarget, target);
        if(!targetState)
          continue;
        this.writeItemFilter(targetState, linkTarget, filter);
      }
    }
  }

  private getItemState(item: PageFilterItem<any, any>, target: 'draft' | 'source'): FilterState<any> | null {
    if(target === 'source')
      return item.source.state;
    return this.states().find(o => o.config === item.source.state.config) ?? null;
  }

  private writeItemFilter(state: FilterState<any>, item: PageFilterItem<any, any>, filter: Filter<any> | null): void {
    const oldValue = this.getItemValue(item, state);
    if(oldValue && filter)
      state.replace(oldValue.uuid, filter);
    else if(oldValue)
      state.remove(oldValue.uuid);
    else if(filter)
      state.add(filter);
  }

  private getItemValue(item: PageFilterItem<any, any>, state: FilterState<any>): Filter<any> | null {
    const filter = state.state();
    if(!filter)
      return null;
    if(filter.type !== 'conditions')
      return filter.field === item.field ? filter : null;
    return filter.filters.find(o => o.type !== 'conditions' && o.field === item.field) ?? null;
  }
}
